import * as React from 'react'

import {
  TextBody,
  Space,
  Button,
  Box,
  upToMd,
  Icon,
  TextHeading,
} from '@ally/metronome-ui'
import { muiErrorFill } from '@ally/metronome-icons'
import { LoginHeading } from '@ally/ally-login'

import { ErrorBanner } from '../ErrorBanner'
import { useTransmit } from '../../TransmitContext'
import { CallUsModal } from '../../Modals'

interface FullPageErrorBannerProps {
  content?: string
}

const FullPageErrorBanner: React.FC<FullPageErrorBannerProps> = ({
  content = 'Online Access Temporarily Blocked',
}) => {
  return <ErrorBanner>{content}</ErrorBanner>
}

export const createUrl = (newPath: string): string => {
  const { host, protocol } = window.location
  return `${protocol}//${host}${newPath}`
}

export const PasswordLockoutContent = (): React.ReactElement => {
  const { navigateToResetPassword, isInPortal } = useTransmit()

  const handleResetClick = (): void => {
    if (isInPortal) window.location.assign(createUrl('/reset-password?ts=true'))
    else navigateToResetPassword()
  }

  return (
    <>
      <Space my="md">
        <TextHeading tag="h1" size="lg" focusOnMount>
          We&apos;ve locked your account.
        </TextHeading>
      </Space>
      <Box display="flex" flexDirection="column">
        <Box display="flex" flexDirection="row" mb="md">
          <Icon icon={muiErrorFill} fill="error" mr="xs" size="lg" />
          <Box mb="md" display="flex" width={upToMd<any, any>(12, 6) as any}>
            <TextBody tag="p" size="sm">
              You entered an incorrect username or password too many times.
              Double-check your username or reset your password to continue -
              it&apos;ll only take a few minutes.
            </TextBody>
          </Box>
        </Box>
        <Button text="Reset Your Password" onClick={handleResetClick} />
      </Box>
    </>
  )
}

export const BlockedLockoutContent = (): React.ReactElement => {
  return (
    <>
      <FullPageErrorBanner />
      <Space my="md">
        <TextBody tag="p" size="sm">
          Your online access has been blocked due to too many unsuccessful
          attempts. Please <CallUsModal /> us 24/7 for help.
        </TextBody>
      </Space>
    </>
  )
}

export const DefaultContent = (): React.ReactElement => {
  return (
    <>
      <FullPageErrorBanner content="Request cannot be processed" />
      <Space my="md">
        <TextBody tag="p" size="sm">
          We&apos;re unable to process your request at this time. Please{' '}
          <CallUsModal /> us 24/7 for help.
        </TextBody>
      </Space>
    </>
  )
}

export const StragglerUserContent = (): React.ReactElement => {
  return (
    <>
      <FullPageErrorBanner content="Additional Security Setup Required" />
      <Space my="md">
        <TextBody tag="p" size="sm">
          It&apos;s been a while since you logged in, so for your protection, we
          have blocked online access to your account until you upgrade to our
          new security features. Please <CallUsModal /> us 24/7, and we&apos;ll
          help you.
        </TextBody>
      </Space>
    </>
  )
}

export const DeceasedUserOrUsernameBlockedContent = (): React.ReactElement => {
  return (
    <>
      <FullPageErrorBanner />
      <Space my="md">
        <TextBody tag="p" size="sm">
          Your username is currently blocked, inactive or not enrolled. Please{' '}
          <CallUsModal /> us 24/7 for help.
        </TextBody>
      </Space>
    </>
  )
}

export const AdditionalSecuritySetupRequiredContent = (): React.ReactElement => {
  return (
    <>
      <FullPageErrorBanner content="Additional Security Setup Required" />
      <Space my="md">
        <TextBody tag="p" size="sm">
          For your protection, you must set up additional security features
          before updating your password. Please <CallUsModal /> us 24/7 for
          help.
        </TextBody>
      </Space>
    </>
  )
}

export const AuthSessionExpiredContent = (): React.ReactElement => {
  return (
    <>
      <FullPageErrorBanner content="Session Expired" />
      <Space my="md">
        <TextBody tag="p" size="sm">
          Your session expires after 20 minutes. Please try logging in again, or{' '}
          <CallUsModal /> us 24/7 for help.
        </TextBody>
      </Space>
    </>
  )
}

export const RSABlackListContent = (): React.ReactElement => {
  return (
    <>
      <FullPageErrorBanner content="Online Access Blocked" />
      <Space my="md">
        <TextBody tag="p" size="sm">
          We&apos;re unable to process your request. Please <CallUsModal /> us
          24/7 for help.
        </TextBody>
      </Space>
    </>
  )
}

export const EmailSunsetFinalSwitch = (): React.ReactElement => {
  return (
    <>
      <LoginHeading title="We're missing some info." />
      <Space my="md">
        <TextBody tag="p" size="sm">
          <CallUsModal triggerContent="Give us a call" /> to update your
          security settings and access your account.
        </TextBody>
      </Space>
    </>
  )
}
